import { Link } from 'react-router-dom';
import { useOrg } from '../components/OrgContext';
import { useVoice } from '../components/VoiceModal';
import { useT } from '../i18n';
import { needsRowsFor, needsListPath, needsRowLabelKey, newTodayPath } from '../lib/needsToday';
import { SHOW_UNCLASSIFIED_ON_HOME } from './homeConfig';
import { discrepancyPath, docLabel, problemLabel, receivedLink, unclassifiedLink } from './todayFormat';
import { useToday } from './useToday';
import { L } from './tokens';
import { BigMicIcon, CasesTileIcon, InventoryTileIcon, ReportsTileIcon } from './icons';
import { useNeedsToday } from './useNeedsToday';
import { useViewportKind } from './useViewport';

// Leo's Home boards: agent (Cases, Reports, a tall "New today" tile) and distributor (Cases, Reports, Inventory,
// "New today"), the Ask mic in the centre. At 1440 px exactly his sizes; smaller screens use the layouts approved in the
// small-screen preview (laptop, tablet, phone).

const AGENT = {
  wide: { tile: 250, gap: 22, main: '72px 72px 56px', mic: 108, needs: { pad: '32px 36px 24px 76px', title: 28, row: 18, rowPad: 14 } },
  laptop: { tile: 220, gap: 22, main: '56px 40px 44px', mic: 96, needs: { pad: '28px 30px 20px 64px', title: 28, row: 18, rowPad: 14 } },
  tablet: { tile: 220, gap: 20, main: '36px 32px 36px', mic: 88, needs: { pad: '24px 24px 16px 60px', title: 24, row: 16, rowPad: 10 } },
  phone: { tile: 140, gap: 14, main: '20px 16px 104px', mic: 72, needs: { pad: '18px 20px 8px', title: 24, row: 17, rowPad: 12 } },
};
const DIST = {
  wide: { tile: 270, gap: 22, main: '72px 72px 56px', mic: 108, needs: { pad: '20px 28px 14px 70px', title: 24, row: 16, rowPad: 8 } },
  laptop: { tile: 220, gap: 22, main: '56px 40px 44px', mic: 96, needs: { pad: '20px 28px 14px 70px', title: 24, row: 16, rowPad: 8 } },
  tablet: { tile: 220, gap: 20, main: '36px 32px 36px', mic: 88, needs: { pad: '14px 20px 10px 54px', title: 22, row: 15, rowPad: 6 } },
  phone: { tile: 100, gap: 14, main: '20px 16px 104px', mic: 72, needs: { pad: '18px 20px 8px', title: 24, row: 17, rowPad: 12 } },
};

function LauncherTile({ to, Icon, label, height, phone, style }) {
  return (
    <Link to={to} style={{
      background: L.card, border: `1px solid ${L.border}`, borderRadius: 22, height, boxSizing: 'border-box', display: 'flex',
      flexDirection: phone ? 'row' : 'column', alignItems: 'center', justifyContent: 'center', gap: phone ? 16 : 12,
      textDecoration: 'none', color: L.text, ...style,
    }}>
      <div style={{ width: phone ? 48 : 64, height: phone ? 48 : 64, borderRadius: 16, background: L.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={phone ? 26 : 30} />
      </div>
      <div style={{ fontFamily: L.serif, fontSize: phone ? 24 : 28, fontWeight: 500, fontStyle: 'italic' }}>{label}</div>
    </Link>
  );
}

// The agent Home "Today" tile: what arrived today by document type, and the new discrepancies today by problem type. Types come from the data; a section with nothing shows 0.
function TodayTile({ sizes, style, t, can }) {
  const { orgId } = useOrg();
  const today = useToday(orgId);
  const d = today.data;
  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: 12, padding: `${sizes.rowPad}px 0`, borderTop: `1px solid ${L.rowLine}`, textDecoration: 'none', color: L.text, fontSize: sizes.row,
  };
  const heading = (text, id) => <h3 id={id} style={{ margin: `${sizes.rowPad}px 0 0`, fontSize: 12, fontWeight: 600, letterSpacing: 1.3, color: L.muted, textTransform: 'uppercase' }}>{text}</h3>;
  const row = (key, label, count, to, state) => (
    <Link key={key} to={to} state={state} data-testid={`today-row-${key}`} style={rowStyle}>
      <span style={{ flexGrow: 1 }}>{label}</span>
      <span style={{ fontWeight: 600, color: L.accent }}>{count}</span>
    </Link>
  );
  const zero = (key) => <div key={key} data-testid={`today-zero-${key}`} style={{ ...rowStyle, color: L.muted }}><span style={{ flexGrow: 1 }} /><span style={{ fontWeight: 600 }}>0</span></div>;
  return (
    <section aria-labelledby="today-title" data-testid="today-tile" style={{
      background: L.card, border: `1px solid ${L.border}`, borderRadius: 22, boxSizing: 'border-box', padding: sizes.pad,
      display: 'flex', flexDirection: 'column', justifyContent: 'center', ...style,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingBottom: Math.round(sizes.rowPad * 0.7) }}>
        <h2 id="today-title" style={{ margin: 0, fontFamily: L.serif, fontStyle: 'italic', fontSize: sizes.title, fontWeight: 500 }}>{t('today.title')}</h2>
      </div>
      {today.status === 'loading' && <div role="status" data-testid="today-loading" style={{ color: L.muted, fontSize: sizes.row }}>…</div>}
      {today.status === 'error' && <div role="alert" data-testid="today-error" style={{ color: L.muted, fontSize: sizes.row }}>{t('today.error')}</div>}
      {today.status === 'ready' && (
        <>
          {heading(t('today.received'), 'today-received')}
          <div data-testid="today-received">
            {d.received.length ? d.received.map((r) => { const l = receivedLink(r.type, d.today); return row(`rec-${r.type}`, docLabel(t, r.type), r.count, l.to, l.state); }) : zero('received')}
            {SHOW_UNCLASSIFIED_ON_HOME && can('documents.upload') && d.unclassified > 0 && (() => { const l = unclassifiedLink(); return row('unclassified', t('today.unclassified'), d.unclassified, l.to, l.state); })()}
          </div>
          {heading(t('today.discrepancies'), 'today-discrepancies')}
          <div data-testid="today-discrepancies">
            {d.discrepancies.length ? d.discrepancies.map((x) => row(`dis-${x.type}`, problemLabel(t, x.type), x.count, discrepancyPath(x.type))) : zero('discrepancies')}
          </div>
        </>
      )}
    </section>
  );
}

function NeedsTile({ rows, loaded, sizes, style, t, kind }) {
  return (
    <section aria-labelledby="needs-today-title" style={{
      background: L.card, border: `1px solid ${L.border}`, borderRadius: 22, boxSizing: 'border-box', padding: sizes.pad,
      display: 'flex', flexDirection: 'column', justifyContent: 'center', ...style,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingBottom: Math.round(sizes.rowPad * 0.7) }}>
        <h2 id="needs-today-title" style={{ margin: 0, fontFamily: L.serif, fontStyle: 'italic', fontSize: sizes.title, fontWeight: 500 }}>{t('needs.title')}</h2>
      </div>
      {needsRowsFor(kind).filter((key) => !(rows[key] && rows[key].off)).map((key) => {
        const value = !loaded ? '' : rows[key] && rows[key].new != null ? t('needs.new', { n: rows[key].new }) : t('needs.unavailable');
        return (
          <Link key={key} to={kind === 'agent' ? newTodayPath(key) : needsListPath(key)} data-testid={`needs-row-${key}`} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: `${sizes.rowPad}px 0`, borderTop: `1px solid ${L.rowLine}`,
            textDecoration: 'none', color: L.text, fontSize: sizes.row,
          }}>
            <span style={{ flexGrow: 1 }}>{t(needsRowLabelKey(key, kind))}</span>
            <span style={{ fontWeight: 600, color: L.accent }}>{value}</span>
          </Link>
        );
      })}
    </section>
  );
}

export default function HomeBoard({ kind }) {
  const t = useT();
  const vk = useViewportKind();
  const { orgId, can } = useOrg();
  const { openVoice, voiceActive } = useVoice();
  const { loaded, rows } = useNeedsToday(orgId, kind !== 'agent');      // the five older rows: the distributor's tile
  const phone = vk === 'phone';
  const S = (kind === 'agent' ? AGENT : DIST)[vk];
  const showReports = can('reports.financial');

  const tile = (key, Icon, to, label, style) => <LauncherTile key={key} to={to} Icon={Icon} label={label} height={S.tile} phone={phone} style={style} />;
  const needs = kind === 'agent'
    ? <TodayTile key="needs" sizes={S.needs} t={t} can={can} style={null} />
    : <NeedsTile key="needs" rows={rows} loaded={loaded} sizes={S.needs} t={t} kind={kind} style={null} />;

  let grid;
  if (phone) {
    grid = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: S.gap }}>
        {needs}
        {tile('cases', CasesTileIcon, '/cases', 'Cases')}
        {showReports && tile('reports', ReportsTileIcon, '/reports', 'Reports')}
        {kind !== 'agent' && tile('inventory', InventoryTileIcon, '/inventory', 'Inventory')}
      </div>
    );
  } else if (kind === 'agent') {
    grid = (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gridTemplateRows: `${S.tile}px ${S.tile}px`, gap: S.gap }}>
        {tile('cases', CasesTileIcon, '/cases', 'Cases', { gridColumn: 1, gridRow: showReports ? 1 : '1 / span 2', height: showReports ? S.tile : '100%' })}
        <TodayTile sizes={S.needs} t={t} can={can} style={{ gridColumn: 2, gridRow: '1 / span 2' }} />
        {showReports && tile('reports', ReportsTileIcon, '/reports', 'Reports', { gridColumn: 1, gridRow: 2 })}
      </div>
    );
  } else {
    grid = (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: S.gap }}>
        {tile('cases', CasesTileIcon, '/cases', 'Cases')}
        {showReports && tile('reports', ReportsTileIcon, '/reports', 'Reports')}
        {tile('inventory', InventoryTileIcon, '/inventory', 'Inventory')}
        <NeedsTile rows={rows} loaded={loaded} sizes={S.needs} t={t} kind={kind} style={{ height: S.tile }} />
      </div>
    );
  }

  const mic = (
    <button type="button" onClick={openVoice} aria-label={t('home.askByVoice')} data-testid="home-mic" style={{
      position: phone ? 'fixed' : 'absolute', ...(phone ? { right: 16, bottom: 16, boxShadow: '0 6px 18px rgba(46,33,22,0.3)' } : { left: '50%', top: '50%', margin: `${-S.mic / 2}px 0 0 ${-S.mic / 2}px` }),
      width: S.mic, height: S.mic, borderRadius: '50%', background: L.accent, border: `8px solid ${L.bg}`, boxSizing: 'border-box',
      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, zIndex: 30,
    }}>
      <BigMicIcon size={phone ? 28 : 36} />
      {voiceActive && (
        <span data-testid="mic-dot" role="img" aria-label={t('header.conversation')}
          style={{ position: 'absolute', top: 2, right: 2, width: 14, height: 14, borderRadius: '50%', background: L.green, border: `3px solid ${L.bg}` }} />
      )}
    </button>
  );

  return (
    <main style={{ flexGrow: 1, boxSizing: 'border-box', padding: S.main, display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ position: 'relative' }}>
        {grid}
        {mic}
      </div>
    </main>
  );
}
